/* ==========================================================================
   The Crafty Pebblers — Theme JS (vanilla, no frameworks)
   ========================================================================== */

(function () {
  'use strict';

  /* -----------------------------------------------------------------------
     Product accordions: open on desktop, collapsed on mobile.

     Baymard's product page testing found participants "repeatedly
     overlook core product page content" that can only be reached behind
     a tab or a closed section. Their recommendation splits by device:
     Expanded Sections on desktop, Collapsed Sections on mobile for
     longer pages. This page is long on a phone and not on a laptop, so
     it follows both.

     Opened once at load rather than watched on resize: reopening
     sections under someone who has deliberately closed them would be
     worse than the problem being solved.
     ----------------------------------------------------------------------- */
  function openAccordionsOnDesktop() {
    if (!window.matchMedia('(min-width: 900px)').matches) return;
    $$('.product-accordion__item').forEach((item) => {
      item.open = true;
    });
  }

  /* -----------------------------------------------------------------------
     Utility helpers
     ----------------------------------------------------------------------- */
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

  function fetchJSON(url, opts = {}) {
    return fetch(url, {
      ...opts,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...opts.headers },
    }).then((r) => {
      if (!r.ok) throw new Error(r.statusText);
      return r.json();
    });
  }

  /* -----------------------------------------------------------------------
     AJAX Cart helpers
     ----------------------------------------------------------------------- */
  const Cart = {
    get() {
      return fetchJSON('/cart.js');
    },
    add(id, quantity = 1) {
      return fetchJSON('/cart/add.js', {
        method: 'POST',
        body: JSON.stringify({ items: [{ id: Number(id), quantity }] }),
      });
    },
    change(line, quantity) {
      return fetchJSON('/cart/change.js', {
        method: 'POST',
        body: JSON.stringify({ line, quantity }),
      });
    },
    clear() {
      return fetchJSON('/cart/clear.js', { method: 'POST', body: '{}' });
    },
  };

  /* -----------------------------------------------------------------------
     Cart Drawer
     ----------------------------------------------------------------------- */
  class CartDrawer {
    constructor() {
      this.drawer = $('cart-drawer') || $('[data-cart-drawer]');
      this.overlay = $('[data-cart-overlay]');
      this.body = $('[data-cart-body]');
      this.footer = $('[data-cart-footer]');
      this.countEls = $$('[data-cart-count]');
      if (!this.drawer) return;

      this.bindEvents();
    }

    bindEvents() {
      // Open
      $$('[data-cart-toggle]').forEach((btn) =>
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.open();
        })
      );
      // Close
      $$('[data-cart-close]').forEach((btn) =>
        btn.addEventListener('click', () => this.close())
      );
      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.close());
      }
      // Esc
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) this.close();
      });
    }

    isOpen() {
      return this.drawer && this.drawer.getAttribute('aria-hidden') === 'false';
    }

    open() {
      if (!this.drawer) return;
      this.drawer.setAttribute('aria-hidden', 'false');
      if (this.overlay) this.overlay.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      this.refresh();
    }

    close() {
      if (!this.drawer) return;
      this.drawer.setAttribute('aria-hidden', 'true');
      if (this.overlay) this.overlay.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    async refresh() {
      try {
        const cart = await Cart.get();
        this.renderItems(cart);
        this.updateCount(cart.item_count);
      } catch (err) {
        console.error('Cart refresh failed', err);
      }
    }

    updateCount(count) {
      this.countEls.forEach((el) => {
        el.textContent = count;
        el.style.display = count > 0 ? '' : 'none';
      });
    }

    renderItems(cart) {
      if (!this.body) return;

      if (cart.item_count === 0) {
        this.body.innerHTML = '<p class="cart-drawer__empty">Your cart is empty.</p>';
        if (this.footer) this.footer.style.display = 'none';
        return;
      }

      if (this.footer) this.footer.style.display = '';

      this.body.innerHTML = cart.items
        .map(
          (item, i) => `
        <div class="cart-drawer__item" data-line="${i + 1}">
          <img src="${this.imgURL(item.image, 160)}" alt="${this.esc(item.title)}" class="cart-drawer__item-image" width="80" height="80" loading="lazy">
          <div class="cart-drawer__item-info">
            <p class="cart-drawer__item-title">${this.esc(item.product_title)}</p>
            ${item.variant_title && item.variant_title !== 'Default Title' ? `<p class="cart-drawer__item-variant">${this.esc(item.variant_title)}</p>` : ''}
            <div class="cart-drawer__item-actions">
              <button class="cart-drawer__qty-btn" data-drawer-qty-change="-1" data-line="${i + 1}">−</button>
              <span class="cart-drawer__qty">${item.quantity}</span>
              <button class="cart-drawer__qty-btn" data-drawer-qty-change="1" data-line="${i + 1}">+</button>
            </div>
            <button class="cart-drawer__item-remove" data-drawer-remove data-line="${i + 1}">Remove</button>
          </div>
          <span class="cart-drawer__item-price">${this.money(item.final_line_price)}</span>
        </div>`
        )
        .join('');

      // Subtotal
      const subtotalEl = $('[data-cart-subtotal]');
      if (subtotalEl) subtotalEl.textContent = this.money(cart.total_price);

      // Qty buttons
      $$('[data-drawer-qty-change]', this.body).forEach((btn) =>
        btn.addEventListener('click', async () => {
          const line = Number(btn.dataset.line);
          const delta = Number(btn.dataset.drawerQtyChange);
          const item = cart.items[line - 1];
          const newQty = Math.max(0, item.quantity + delta);
          await Cart.change(line, newQty);
          this.refresh();
        })
      );

      // Remove buttons
      $$('[data-drawer-remove]', this.body).forEach((btn) =>
        btn.addEventListener('click', async () => {
          await Cart.change(Number(btn.dataset.line), 0);
          this.refresh();
        })
      );
    }

    imgURL(url, size) {
      if (!url) return '';
      return url.replace(/(\.\w+)(\?|$)/, `_${size}x$1$2`);
    }

    esc(str) {
      const d = document.createElement('div');
      d.textContent = str;
      return d.innerHTML;
    }

    money(cents) {
      return '€' + (cents / 100).toFixed(2);
    }
  }

  /* -----------------------------------------------------------------------
     Product Form (Web Component)
     ----------------------------------------------------------------------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;

      this.form.addEventListener('submit', this.onSubmit.bind(this));

      // Variant option selectors
      this.optionSelects = $$('.product-detail__option-select', this);
      this.variantSelect = $('[data-variant-select]', this);
      this.optionSelects.forEach((sel) =>
        sel.addEventListener('change', () => this.onOptionChange())
      );

      // Quantity buttons
      const minusBtn = $('[data-quantity-minus]', this);
      const plusBtn = $('[data-quantity-plus]', this);
      const qtyInput = $('input[name="quantity"]', this);
      if (minusBtn && plusBtn && qtyInput) {
        minusBtn.addEventListener('click', () => {
          qtyInput.value = Math.max(1, Number(qtyInput.value) - 1);
        });
        plusBtn.addEventListener('click', () => {
          qtyInput.value = Number(qtyInput.value) + 1;
        });
      }
    }

    onOptionChange() {
      if (!this.variantSelect) return;
      const selected = this.optionSelects.map((s) => s.value);
      const options = [...this.variantSelect.options];
      for (const opt of options) {
        const parts = opt.textContent.trim().split(' / ');
        if (parts.length === selected.length && parts.every((p, i) => p === selected[i])) {
          this.variantSelect.value = opt.value;
          // Update price
          const priceEl =
            $('.product-detail__price--sale') ||
            $('.product-detail__price--current');
          if (priceEl && opt.dataset.price) {
            priceEl.textContent = opt.dataset.price;
          }
          // Update submit button
          const submitBtn = $('button[type="submit"]', this.form);
          if (submitBtn) {
            // Keep whatever wording the theme rendered. Hard-coding it here
            // turned "Add to Basket" into "Add to Cart" the moment a colour
            // was chosen, on the one button that has to look deliberate.
            if (!submitBtn.dataset.labelDefault) {
              submitBtn.dataset.labelDefault = submitBtn.textContent.trim();
            }
            submitBtn.disabled = opt.disabled;
            submitBtn.textContent = opt.disabled
              ? 'Sold'
              : submitBtn.dataset.labelDefault;
          }
          // Show that variant's photograph. Choosing a green hat and still
          // looking at the red one is the whole sale gone. The thumbnail
          // already knows how to swap the main image, srcset and alt, so
          // click it rather than repeat that here. -1, or a product whose
          // variants have no photographs of their own, leaves the gallery
          // exactly as it was.
          const imageIndex = opt.dataset.imageIndex;
          if (imageIndex !== undefined && imageIndex !== '-1') {
            const thumb = $(`[data-thumb-index="${imageIndex}"]`);
            if (thumb) thumb.click();
          }
          break;
        }
      }
    }

    async onSubmit(e) {
      e.preventDefault();
      const submitBtn = $('button[type="submit"]', this.form);
      if (!submitBtn || submitBtn.disabled) return;

      const origText = submitBtn.textContent;
      submitBtn.disabled = true;
      submitBtn.innerHTML = '<span class="loading-spinner"></span>';

      try {
        const id = this.variantSelect
          ? this.variantSelect.value
          : $('input[name="id"]', this.form)?.value;
        const qty = Number($('input[name="quantity"]', this.form)?.value || 1);
        await Cart.add(id, qty);

        submitBtn.textContent = 'Added!';
        setTimeout(() => {
          submitBtn.textContent = origText;
          submitBtn.disabled = false;
        }, 1500);

        // Open cart drawer
        if (window.__cartDrawer) window.__cartDrawer.open();
      } catch (err) {
        console.error('Add to cart failed', err);
        submitBtn.textContent = origText;
        submitBtn.disabled = false;
      }
    }
  }

  if (!customElements.get('product-form')) {
    customElements.define('product-form', ProductForm);
  }

  /* -----------------------------------------------------------------------
     Mobile Menu
     ----------------------------------------------------------------------- */
  function initMobileMenu() {
    const menu = $('[data-mobile-menu]');
    if (!menu) return;

    $$('[data-menu-toggle]').forEach((btn) =>
      btn.addEventListener('click', () => {
        menu.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
      })
    );

    $$('[data-menu-close]').forEach((btn) =>
      btn.addEventListener('click', () => {
        menu.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
      })
    );

    // Close on overlay click
    $$('[data-menu-overlay]', menu).forEach((el) =>
      el.addEventListener('click', () => {
        menu.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
      })
    );

    // Dropdown toggles
    $$('[data-mobile-dropdown-toggle]', menu).forEach((btn) => {
      btn.addEventListener('click', () => {
        const expanded = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!expanded));
        const sub = btn.nextElementSibling;
        if (sub) sub.setAttribute('aria-hidden', String(expanded));
      });
    });
  }

  /* -----------------------------------------------------------------------
     Search Overlay
     ----------------------------------------------------------------------- */
  function initSearchOverlay() {
    const overlay = $('[data-search-overlay]');
    if (!overlay) return;

    $$('[data-search-toggle]').forEach((btn) =>
      btn.addEventListener('click', () => {
        overlay.setAttribute('aria-hidden', 'false');
        const input = $('input', overlay);
        if (input) setTimeout(() => input.focus(), 100);
      })
    );

    $$('[data-search-close]').forEach((btn) =>
      btn.addEventListener('click', () => {
        overlay.setAttribute('aria-hidden', 'true');
      })
    );

    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) overlay.setAttribute('aria-hidden', 'true');
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && overlay.getAttribute('aria-hidden') === 'false') {
        overlay.setAttribute('aria-hidden', 'true');
      }
    });
  }

  /* -----------------------------------------------------------------------
     Header Scroll Effect
     ----------------------------------------------------------------------- */
  function initHeaderScroll() {
    const header = $('[data-header]');
    if (!header) return;

    let ticking = false;
    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          header.classList.toggle('header--scrolled', window.scrollY > 50);
          ticking = false;
        });
        ticking = true;
      }
    });
  }

  /* -----------------------------------------------------------------------
     Announcement Bar Dismiss
     ----------------------------------------------------------------------- */
  function initAnnouncementBar() {
    const bar = $('[data-announcement-bar]');
    const closeBtn = $('[data-announcement-close]');
    if (!bar || !closeBtn) return;

    if (sessionStorage.getItem('announcement-dismissed') === 'true') {
      bar.remove();
      return;
    }

    closeBtn.addEventListener('click', () => {
      bar.style.transition = 'opacity 0.3s, max-height 0.3s';
      bar.style.opacity = '0';
      bar.style.maxHeight = '0';
      bar.style.overflow = 'hidden';
      setTimeout(() => bar.remove(), 300);
      sessionStorage.setItem('announcement-dismissed', 'true');
    });
  }

  /* -----------------------------------------------------------------------
     Scroll Animations (IntersectionObserver)
     ----------------------------------------------------------------------- */
  function initScrollAnimations() {
    const els = $$('[data-animate]');
    if (!els.length) return;

    const show = (el) => el.classList.add('is-visible');

    if (!('IntersectionObserver' in window)) {
      els.forEach(show);
      return;
    }

    // threshold 0 and a POSITIVE bottom margin, not 0.1 and -40px.
    //
    // Pat, 1 November 2026: "when you land on a page it is empty until you
    // scroll". The cause was threshold 0.1 on elements taller than the screen.
    // A collection grid is one [data-animate] element about 2000px tall; a
    // phone showing the top 400px of it is showing 20%, but a grid of 30
    // pieces is 5000px and the same phone shows 8%, under the threshold, so
    // the whole grid stayed at opacity 0 until the reader scrolled. The
    // negative bottom margin made it worse by shrinking the trigger area.
    //
    // threshold 0 fires on the first pixel, whatever the element's height, and
    // the 300px bottom margin brings in anything just under the fold so it has
    // already faded by the time it is scrolled to.
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            show(entry.target);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0, rootMargin: '0px 0px 300px 0px' }
    );

    els.forEach((el) => observer.observe(el));

    // The net. Anything in or near the first screen is shown once the page has
    // loaded, whatever the observer did. Nothing a customer needs to see,
    // least of all a product grid or the basket, should depend on one API
    // firing correctly.
    const sweep = () => {
      els.forEach((el) => {
        if (el.classList.contains('is-visible')) return;
        if (el.getBoundingClientRect().top < window.innerHeight * 1.25) show(el);
      });
    };
    if (document.readyState === 'complete') sweep();
    else window.addEventListener('load', sweep, { once: true });
  }

  /* -----------------------------------------------------------------------
     Product Image Gallery + Lightbox
     ----------------------------------------------------------------------- */
  function initProductGallery() {
    const mainImage = $('[data-product-main-image] img');
    const thumbs = $$('[data-thumb-index]');
    if (!mainImage || !thumbs.length) return;

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        // Swap the srcset too, not just src. Setting src alone leaves the
        // old srcset in place and the browser keeps showing the previous
        // picture's candidates, which on a Retina screen means the clicked
        // thumbnail loads soft or not at all.
        mainImage.srcset = thumb.dataset.thumbSrcset || '';
        mainImage.src = thumb.dataset.thumbSrc;
        mainImage.alt = thumb.dataset.thumbAlt || '';
        thumbs.forEach((t) => t.classList.remove('product-detail__thumb--active'));
        thumb.classList.add('product-detail__thumb--active');
      });
    });

    // Lightbox
    const lightbox = $('[data-lightbox]');
    const lbImage = $('[data-lightbox-image]');
    const lbClose = $('[data-lightbox-close]');
    const lbTrigger = $('[data-lightbox-trigger]');

    if (lightbox && lbImage && lbTrigger) {
      lbTrigger.addEventListener('click', () => {
        lbImage.src = mainImage.src;
        lbImage.alt = mainImage.alt;
        lightbox.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
      });

      const closeLightbox = () => {
        lightbox.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
      };

      if (lbClose) lbClose.addEventListener('click', closeLightbox);
      lightbox.addEventListener('click', (e) => {
        if (e.target === lightbox || e.target.classList.contains('lightbox__content'))
          closeLightbox();
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && lightbox.getAttribute('aria-hidden') === 'false')
          closeLightbox();
      });
    }
  }

  /* -----------------------------------------------------------------------
     Product Recommendations
     ----------------------------------------------------------------------- */
  function initProductRecommendations() {
    const section = $('[data-product-recommendations]');
    if (!section) return;

    const productId = section.dataset.productId;
    const limit = section.dataset.limit || 4;
    const grid = $('[data-recommendations-grid]', section);
    if (!productId || !grid) return;

    fetch(`/recommendations/products.json?product_id=${productId}&limit=${limit}`)
      .then((r) => r.json())
      .then((data) => {
        if (!data.products || !data.products.length) {
          section.style.display = 'none';
          return;
        }
        grid.innerHTML = data.products
          .map(
            (p) => `
          <a href="${p.url}" class="product-card">
            <div class="product-card__media">
              ${p.featured_image ? `<img src="${p.featured_image}" alt="${esc(p.title)}" class="product-card__image" loading="lazy" width="400" height="400">` : ''}
            </div>
            <div class="product-card__info">
              ${p.product_type ? `<span class="product-card__type">${esc(p.product_type)}</span>` : ''}
              <h3 class="product-card__title">${esc(p.title)}</h3>
              <span class="product-card__price">€${(p.price / 100).toFixed(2)}</span>
            </div>
          </a>`
          )
          .join('');
      })
      .catch((err) => console.error('Recommendations failed', err));

    function esc(str) {
      const d = document.createElement('div');
      d.textContent = str;
      return d.innerHTML;
    }
  }

  /* -----------------------------------------------------------------------
     Collection Sort
     ----------------------------------------------------------------------- */
  function initCollectionSort() {
    const sortSelect = $('[data-sort-select]');
    if (!sortSelect) return;

    sortSelect.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sortSelect.value);
      window.location.href = url.toString();
    });
  }

  /* -----------------------------------------------------------------------
     Cart Page Interactions
     ----------------------------------------------------------------------- */
  function initCartPage() {
    // Remove buttons
    $$('[data-remove-item]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const line = Number(btn.dataset.line);
        await Cart.change(line, 0);
        window.location.reload();
      });
    });

    // Quantity change buttons on cart page
    $$('[data-qty-change]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const line = Number(btn.dataset.line);
        const delta = Number(btn.dataset.qtyChange);
        const input = $(`input[data-line="${line}"]`);
        if (!input) return;
        const newQty = Math.max(0, Number(input.value) + delta);
        input.value = newQty;
        await Cart.change(line, newQty);
        window.location.reload();
      });
    });
  }

  /* -----------------------------------------------------------------------
     Init everything on DOM ready
     ----------------------------------------------------------------------- */
  document.addEventListener('DOMContentLoaded', () => {
    openAccordionsOnDesktop();
    window.__cartDrawer = new CartDrawer();
    initMobileMenu();
    initSearchOverlay();
    initHeaderScroll();
    initAnnouncementBar();
    initScrollAnimations();
    initProductGallery();
    initProductRecommendations();
    initCollectionSort();
    initCartPage();
  });
})();
